import React from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorBoundary, FallbackProps } from '../../../components/error-boundary';
import Footer from '../../../components/Footer';
import StickyHeader from '../../app/StickyHeader';
import { EmptyPage } from '../../ats-pages/page/empty-page';
import { useDetails } from '../details-context';
import css from './details-error.module.scss';

interface DetailsErrorBoundaryProps {
  label: string;
  children: React.ReactNode;
}

export function DetailsErrorBoundary({ label, children }: DetailsErrorBoundaryProps) {
  const { selectedContextId } = useDetails('DetailsErrorBoundary');

  return (
    <ErrorBoundary
      resetKeys={[selectedContextId]}
      fallback={(props) => <DetailsErrorFallback label={label} {...props} />}
    >
      {children}
    </ErrorBoundary>
  );
}

export function DetailsRootErrorBoundary({ children }: { children: React.ReactNode }) {
  return <ErrorBoundary fallback={(props) => <DetailsRootErrorFallback {...props} />}>{children}</ErrorBoundary>;
}

export function PageErrorBoundry({ children }: { children: React.ReactNode }) {
  return (
    <ErrorBoundary
      fallback={(props) => (
        <>
          <StickyHeader />
          <EmptyPage label={''} noDescription />
          <DetailsRootErrorFallback {...props} />
          <Footer />
        </>
      )}
    >
      {children}
    </ErrorBoundary>
  );
}

function DetailsRootErrorFallback({ error }: FallbackProps) {
  console.error(error);
  const { t } = useTranslation();
  return (
    <div className={css.root}>
      <h1 className={css.heading}>{t('oops, something went wrong')}</h1>
      <p className={css.message}>{process.env.NODE_ENV === 'development' ? error.message : t('page not found')}</p>
    </div>
  );
}

interface DetailsErrorFallbackProps extends FallbackProps {
  label: string;
}

function DetailsErrorFallback({ label, error, reset }: DetailsErrorFallbackProps) {
  if (process.env.NODE_ENV !== 'production') {
    return (
      <div className={css.error}>
        <h5>Failed to load {label} 🖕</h5>
        <p className={css.reason}>{error.message ?? 'unknown'}</p>
        <button className={css.button} onClick={() => reset()}>
          Retry
        </button>
      </div>
    );
  }

  return null;
}
